import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ExpertLevel, EXPERT_LEVEL_CONFIG, FeatureConfig } from '@/constants/expertModeConfig';
import { DEFAULT_SHELL, isValidShellId, type UIShell } from '@/shells/registry';

// Re-exported so existing imports from '@/contexts/SettingsContext' keep working.
export type { UIShell };

/**
 * Generic per-shell preferences bag.
 *
 * Each shell stores its own preferences under its id. Shells declare their
 * own schema and defaults via `useShellPrefs(shellId, defaults)` from
 * `@/shells/shared/useShellPrefs` — there is no central registry of pref
 * shapes here. That's deliberate: it lets a new shell ship its prefs
 * without editing this file.
 *
 * Storage is `Record<shellId, Record<key, value>>`. Values are persisted
 * to localStorage as plain JSON, so stick to JSON-serializable types.
 */
export type ShellPrefsMap = Record<string, Record<string, unknown>>;

interface Settings {
    accessibleTouchTargets: boolean;
    expertLevel: ExpertLevel;
    uiShell: UIShell;
    /** Per-shell preferences keyed by shell id. */
    shellPrefs: ShellPrefsMap;
    // Granular feature overrides - can be expanded later
    featureOverrides?: Partial<FeatureConfig>;
}

interface SettingsContextType {
    settings: Settings;
    features: FeatureConfig;
    toggleAccessibleTouchTargets: () => void;
    setExpertLevel: (level: ExpertLevel) => void;
    setUIShell: (shell: UIShell) => void;
    /**
     * Merge a patch into a shell's prefs. Pass only the keys you want to
     * change. Use `useShellPrefs<T>(shellId, defaults)` instead if you want
     * a typed view of one shell's prefs.
     */
    updateShellPrefs: (shellId: string, patch: Record<string, unknown>) => void;
    updateFeatureOverride: (feature: keyof FeatureConfig, enabled: boolean) => void;
}

const SETTINGS_KEY = 'clando-settings';

const defaultSettings: Settings = {
    accessibleTouchTargets: false,
    expertLevel: 'easy',
    uiShell: DEFAULT_SHELL,
    shellPrefs: {},
};

/**
 * Migrates the legacy per-shell fields (`mstoIntensity`, `mstoPrefs`,
 * `rtmIntensity`, `freelancerIntensity`, `freelancerPrefs`,
 * `valuetimeIntensity`) into the generic `shellPrefs` map.
 *
 * Existing `shellPrefs` keys win — migration only fills gaps.
 */
function migrateLegacyShellPrefs(parsed: Record<string, unknown>): ShellPrefsMap {
    const incoming: ShellPrefsMap = (parsed.shellPrefs as ShellPrefsMap | undefined) ?? {};
    const result: ShellPrefsMap = { ...incoming };

    const seedShell = (id: string, patch: Record<string, unknown>) => {
        result[id] = { ...patch, ...(result[id] ?? {}) };
    };

    if (typeof parsed.mstoIntensity === 'number') {
        seedShell('msto', { intensity: parsed.mstoIntensity });
    }
    if (parsed.mstoPrefs && typeof parsed.mstoPrefs === 'object') {
        seedShell('msto', parsed.mstoPrefs as Record<string, unknown>);
    }
    if (typeof parsed.rtmIntensity === 'number') {
        seedShell('rtm', { intensity: parsed.rtmIntensity });
    }
    if (typeof parsed.freelancerIntensity === 'number') {
        seedShell('freelancer', { intensity: parsed.freelancerIntensity });
    }
    if (parsed.freelancerPrefs && typeof parsed.freelancerPrefs === 'object') {
        seedShell('freelancer', parsed.freelancerPrefs as Record<string, unknown>);
    }
    if (typeof parsed.valuetimeIntensity === 'number') {
        seedShell('valuetime', { intensity: parsed.valuetimeIntensity });
    }

    return result;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [settings, setSettings] = useState<Settings>(() => {
        const saved = localStorage.getItem(SETTINGS_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved) as Record<string, unknown>;
                // Stored shell may be from an older build that no longer registers
                // that id (e.g. an experimental shell was removed). Fall back to
                // the default rather than rendering nothing.
                const storedShell =
                    typeof parsed.uiShell === 'string' && isValidShellId(parsed.uiShell)
                        ? (parsed.uiShell as UIShell)
                        : DEFAULT_SHELL;
                return {
                    ...defaultSettings,
                    ...(parsed as Partial<Settings>),
                    uiShell: storedShell,
                    shellPrefs: migrateLegacyShellPrefs(parsed),
                };
            } catch (e) {
                console.error('Failed to parse settings:', e);
            }
        }
        return defaultSettings;
    });

    useEffect(() => {
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    }, [settings]);

    // Derived features based on expertLevel + overrides
    const features = useMemo(() => {
        const baseConfig = EXPERT_LEVEL_CONFIG[settings.expertLevel];
        return {
            ...baseConfig,
            ...settings.featureOverrides,
        };
    }, [settings.expertLevel, settings.featureOverrides]);

    const toggleAccessibleTouchTargets = useCallback(() => {
        setSettings((prev) => ({
            ...prev,
            accessibleTouchTargets: !prev.accessibleTouchTargets,
        }));
    }, []);

    const setExpertLevel = useCallback((level: ExpertLevel) => {
        setSettings((prev) => ({ ...prev, expertLevel: level }));
    }, []);

    const setUIShell = useCallback((shell: UIShell) => {
        setSettings((prev) => ({ ...prev, uiShell: shell }));
    }, []);

    const updateShellPrefs = useCallback(
        (shellId: string, patch: Record<string, unknown>) => {
            setSettings((prev) => ({
                ...prev,
                shellPrefs: {
                    ...prev.shellPrefs,
                    [shellId]: { ...(prev.shellPrefs[shellId] ?? {}), ...patch },
                },
            }));
        },
        []
    );

    const updateFeatureOverride = useCallback(
        (feature: keyof FeatureConfig, enabled: boolean) => {
            setSettings((prev) => ({
                ...prev,
                featureOverrides: {
                    ...(prev.featureOverrides || {}),
                    [feature]: enabled,
                },
            }));
        },
        []
    );

    const value = useMemo<SettingsContextType>(
        () => ({
            settings,
            features,
            toggleAccessibleTouchTargets,
            setExpertLevel,
            setUIShell,
            updateShellPrefs,
            updateFeatureOverride,
        }),
        [settings, features, toggleAccessibleTouchTargets, setExpertLevel, setUIShell, updateShellPrefs, updateFeatureOverride]
    );

    return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};
